/**
 * UI strings — English (default) and French.
 * @type {Record<'en' | 'fr', Record<string, unknown>>}
 */
export const messages = {
  en: {
    nav: {
      home: 'Home',
      map: 'Map',
      ariaNav: 'Main navigation',
      en: 'EN',
      fr: 'FR',
      langSwitch: 'Language',
    },
    hero: {
      eyebrow: 'Head of Data & AI',
      lead1a: 'With ',
      lead1strong: '10 years of experience turning data',
      lead1b: ' into a strategic lever for growth. As a Head of Data in scale-up environments, I bridge engineering and ',
      lead1underline: 'business ROI',
      lead1end: '.',
      lead2:
        'Outside work, I love football (supporting Arsenal FC is not always easy on the nerves, but I guess that\'s part of the deal 🙄) and running. I like discovering new ground and nudging myself out of my comfort zone.',
      travelMap: 'Travel map',
      photoAlt: 'Clément Tailleur',
      linkedinAria: 'LinkedIn',
    },
    stats: {
      aria: 'Key figures',
      title: 'At a glance',
      intro: 'A few numbers of course 🔢',
      age: '🎂 Age',
      halfMarathons: '🏃🏻 Half marathons',
      pbHalf: '🇨🇦 Personal best half marathon',
      companies: '🏢 Companies I\'ve worked with',
      countries: '🌍 Countries visited (map)',
      trophies: '⚽️ Trophies won by my favorite club',
    },
    dataCharts: {
      sectionAria: 'Head of Data — experience in scale-ups',
      title: 'Head of Data in scale-up environments',
      desc1: 'I\'ve had the opportunity to work across the ',
      descStrong1: 'entire data lifecycle',
      desc2: ', from ',
      descStrong2a: 'deep technical infrastructure',
      desc3: ' to ',
      descStrong2b: 'core business challenges',
      desc4: '. As an engineer, I thrive on solving complex problems, but what truly drives me is being able to ',
      descStrong3: 'measure their actual impact',
      desc5: '.',
      piePlatform: 'Data platform engineering | back-end',
      pieBi: 'BI & Analytics | front-ends',
      emptyPlatform: 'No cloud / platform components in the dataset yet.',
      emptyBi: 'No BI tools in the dataset yet.',
      businessImpact: 'Business Impact',
      macroTime: 'Average time saved / week',
      macroRoi: 'Average ROI',
      managedPeople: 'Managed people',
      departures: '{{pct}}% departures',
      noDeparturesData: 'No departures data',
      businessExperiences: 'Business experiences',
      logosAria: 'Company logos',
      narIntro:
        'I\'ve been lucky to work across very different industries. That variety is a big part of why I love this job.',
      narCs: 'At Contentsquare, I learned from ',
      narCsStrong1: 'Product',
      narCsMid: ' and ',
      narCsStrong2: 'Design',
      narCsEnd: ' experts.',
      narQare: 'At Qare, I deepened my ',
      narQareStrong1: 'Sales',
      narQareMid: ' and ',
      narQareStrong2: 'Operations',
      narQareEnd: ' understanding.',
      narTudigo1: 'At Tudigo, I sharpened ',
      narTudigoStrong1: 'Marketing',
      narTudigo2: ' and learned to ',
      narTudigoStrong2: 'make tough, pivotal decisions for the company',
      narTudigo3:
        ', including advocating for those decisions in committees when the stakes are high.',
      narOutro:
        'Above all, the people around me, experts, peers, and leaders have been what made the difference. Working with humans, not just data, has been the real unlock.',
    },
    cvTable: {
      title: 'Measured impact',
      ariaRegion: 'Measured impact per role',
      colCompany: 'Company',
      colRoi: 'ROI Data',
      colTime: 'Time saved',
      colDesc: 'Description',
      legend:
        'Impact measured only on experiences where I stayed long enough to observe outcomes, including follow-up freelance missions when relevant.',
    },
    notFound: '404 — Sorry, this page could not be found.',
    map: {
      mainAria: 'Travel map',
      statsAria: 'Country counts by category',
      visited: 'Visited countries',
      vacationCountries: 'Vacation countries',
      studiesCountries: 'Studies countries',
      workCountries: 'Work countries',
      halfMarathonCountries: 'Half marathon countries',
      byYear: 'By year',
      yearSubtitleOne: 'Countries visited in the selected year: 1 country.',
      yearSubtitleMany: 'Countries visited in the selected year: {{count}} countries.',
      countriesInYear: 'Countries visited in {{year}}',
      tableNum: '#',
      tableCountry: 'Country',
      yearGroupAria: 'Year',
      yearSr: 'Year',
      prevYear: 'Previous year',
      nextYear: 'Next year',
      legendVisitedYear: 'Visited this year',
    },
    treemap: {
      title: 'Business experiences',
      aria: 'Treemap of missions by company activity type',
      yrSuffix: 'yr',
      yrTotalPhrase: 'yr total',
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      map: 'Carte',
      ariaNav: 'Navigation principale',
      en: 'EN',
      fr: 'FR',
      langSwitch: 'Langue',
    },
    hero: {
      eyebrow: 'Head of Data & IA',
      lead1a: 'Avec ',
      lead1strong: '10 ans d\'expérience à faire parler les données',
      lead1b: ' comme levier stratégique de croissance. En tant que Head of Data en scale-up, je fais le lien entre l\'ingénierie et le ',
      lead1underline: 'ROI business',
      lead1end: '.',
      lead2:
        'En dehors du travail, j\'aime le foot (supporter Arsenal FC, ce n\'est pas toujours reposant pour les nerfs, mais bon 🙄) et la course à pied.',
      travelMap: 'Carte des voyages',
      photoAlt: 'Clément Tailleur',
      linkedinAria: 'LinkedIn',
    },
    stats: {
      aria: 'Chiffres clés',
      title: 'En un coup d\'œil',
      intro: 'Quelques chiffres, évidemment 🔢',
      age: '🎂 Âge',
      halfMarathons: '🏃🏻 Semi-marathons',
      pbHalf: '🇨🇦 Record perso sur semi-marathon',
      companies: '🏢 Entreprises avec lesquelles j\'ai travaillé',
      countries: '🌍 Pays visités (carte)',
      trophies: '⚽️ Trophées remportés par mon club préféré',
    },
    dataCharts: {
      sectionAria: 'Head of Data — expérience en scale-up',
      title: 'Head of Data en environnement scale-up',
      desc1: 'J\'ai eu l\'occasion de couvrir ',
      descStrong1: 'tout le cycle de vie des données',
      desc2: ', de ',
      descStrong2a: 'l\'infra technique',
      desc3: ' aux ',
      descStrong2b: 'enjeux métier',
      desc4: '. En tant qu\'ingénieur, j\'aime résoudre des problèmes complexes ; ce qui me motive surtout, c\'est ',
      descStrong3: 'en mesurer l\'impact réel',
      desc5: '.',
      piePlatform: 'Plateforme data & back-end',
      pieBi: 'BI & analytics | front-ends',
      emptyPlatform: 'Pas encore de composants cloud / plateforme dans les données.',
      emptyBi: 'Pas encore d\'outils BI dans les données.',
      businessImpact: 'Impact business',
      macroTime: 'Temps moyen gagné / semaine',
      macroRoi: 'ROI moyen',
      managedPeople: 'Personnes managées',
      departures: '{{pct}}% de départs',
      noDeparturesData: 'Pas de données sur les départs',
      businessExperiences: 'Expériences business',
      logosAria: 'Logos entreprises',
      narIntro:
        'J\'ai eu la chance de travailler dans des secteurs très différents. Cette diversité est une grande partie de ce que j\'aime dans ce métier.',
      narCs: 'Chez Contentsquare, j\'ai appris auprès d\'experts ',
      narCsStrong1: 'Produit',
      narCsMid: ' et ',
      narCsStrong2: 'Design',
      narCsEnd: '.',
      narQare: 'Chez Qare, j\'ai approfondi ma compréhension ',
      narQareStrong1: 'Sales',
      narQareMid: ' et ',
      narQareStrong2: 'Operations',
      narQareEnd: '.',
      narTudigo1: 'Chez Tudigo, j\'ai renforcé le ',
      narTudigoStrong1: 'Marketing',
      narTudigo2: ' et appris à ',
      narTudigoStrong2: 'prendre des décisions difficiles et structurantes pour l\'entreprise',
      narTudigo3:
        ', y compris en les portant en comité lorsque les enjeux sont élevés.',
      narOutro:
        'Avant tout, ce sont les personnes autour de moi (experts, pairs, dirigeants) qui ont fait la différence. Travailler avec des humains, pas seulement avec des données, a tout changé.',
    },
    cvTable: {
      title: 'Impact mesuré',
      ariaRegion: 'Impact mesuré par rôle',
      colCompany: 'Entreprise',
      colRoi: 'ROI data',
      colTime: 'Temps gagné',
      colDesc: 'Description',
      legend:
        'Impact mesuré uniquement sur les expériences où je suis resté assez longtemps pour observer les résultats, missions freelance de suivi incluses le cas échéant.',
    },
    notFound: '404 — Désolé, cette page est introuvable.',
    map: {
      mainAria: 'Carte des voyages',
      statsAria: 'Nombre de pays par catégorie',
      visited: 'Pays visités',
      vacationCountries: 'Pays (vacances)',
      studiesCountries: 'Pays (études)',
      workCountries: 'Pays (travail)',
      halfMarathonCountries: 'Pays (semi-marathon)',
      byYear: 'Par année',
      yearSubtitleOne: 'Pays visités sur l\'année sélectionnée : un pays.',
      yearSubtitleMany: 'Pays visités sur l\'année sélectionnée : {{count}} pays.',
      countriesInYear: 'Pays visités en {{year}}',
      tableNum: '#',
      tableCountry: 'Pays',
      yearGroupAria: 'Année',
      yearSr: 'Année',
      prevYear: 'Année précédente',
      nextYear: 'Année suivante',
      legendVisitedYear: 'Visité cette année',
    },
    treemap: {
      title: 'Expériences business',
      aria: 'Treemap des missions par type d\'activité',
      yrSuffix: 'ans',
      yrTotalPhrase: 'ans au total',
    },
  },
};
